import {
  DateOnlyIsoString,
  DateOnlyRangeIsoObject,
  DateOnlyRangeIsoString,
  DateRangeObject,
  DateString,
  DateTimeIsoString,
  TimeOfDay
} from '@aviary/store/common/common.types';
import {
  isDate,
  isDateOnlyIsoString,
  isDateOnlyRangeIsoString,
  isDateTimeIsoString,
  isDefined,
  isPopulatedDateString,
  isPopulatedString
} from '@aviary/store/common/typing.helpers';
import {differenceInDays, endOfDay, formatISO, isPast, parse, subDays} from 'date-fns';
import {format} from 'date-fns-tz';

export function toDateOnly(date: DateString): DateOnlyIsoString {
  // If it's already date-only, there's nothing to do.  If it's not a date-time string, just pass it through unchanged.
  if (isDateOnlyIsoString(date) || !isDateTimeIsoString(date)) return date;

  return (date as DateOnlyIsoString).split('T')[0];
}

/**
 * Use this instead of Date.toISOString(), because that function returns a string formatted like '2023-10-12T21:50:14.858Z', while our ISO format
 * (based on our Java API) is just '2023-10-12T21:50:14Z' (no milliseconds).
 */
export function toDateTimeIsoString(date: Date): DateTimeIsoString {
  if (!date) return '';

  return date.toISOString().replace(/\.\d{3}/, '');
}

export function toDateOnlyIsoString(date: Date): DateOnlyIsoString {
  return toDateOnly(toDateTimeIsoString(date));
}

export function toDateOnlyRangeIsoString(startDate?: Date, endDate?: Date): DateOnlyRangeIsoString {
  const startDateString: DateOnlyIsoString = isDefined(startDate) ? toDateOnlyIsoString(startDate) : '';
  const startIsPopulated: boolean = isPopulatedDateString(startDateString);

  const endDateString: DateOnlyIsoString = isDefined(endDate) ? toDateOnlyIsoString(endDate) : '';
  const endIsPopulated: boolean = isPopulatedDateString(endDateString);

  if (startIsPopulated && endIsPopulated) return `${startDateString}..${endDateString}`;
  if (startIsPopulated && !endIsPopulated) return `${startDateString}..${startDateString}`;
  if (!startIsPopulated && endIsPopulated) return `${endDateString}..${endDateString}`;

  return '';
}

export function toDateOnlyRangeIsoObject(range?: DateOnlyRangeIsoString): DateOnlyRangeIsoObject {
  if (!isDateOnlyRangeIsoString(range)) return {start: '', end: ''};

  const [start, end] = range.split('..');

  return {start, end};
}

export function toDateRangeObject(range?: DateOnlyRangeIsoString): DateRangeObject {
  if (!isDateOnlyRangeIsoString(range)) return {};

  const [start, end] = range.split('..');

  return {start: toLocalMidnight(start), end: toLocalMidnight(end)};
}

/**
 * Useful for simulating a "date only" Date (which doesn't exist in JavaScript) by converting a date to midnight -- *in the browser's current time zone*.
 * See https://stackoverflow.com/a/52352512.
 * See "UNIT TESTS" below.
 *
 * And... hopefully with date-fns-tz (date-fns with added support), we can rely on it and get rid of this and a lot of our other custom date code.
 */
export function toLocalMidnight(date: Date | string): Date {
  if (isDate(date)) {
    // Manually construct a date object based on the year, month, and day values of the given date object in order to get a
    // proper local midnight.  We don't use toISOString() here, as it could cause the offset calculation to be performed twice in some cases (multiple date
    // conversions), which won't actually send back the correct day in certain time/timezone combinations.
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  // Avoid using toISOString() here so we don't evaluate against UTC.
  if (isPopulatedString(date)) {
    // Ensure we are *only* working with the yyyy-mm-dd portion of the date string, ignore any time data if present.
    const dateValueOnly = isDateTimeIsoString(date) ? date.split('T')[0] : date;
    return parse(dateValueOnly, 'yyyy-MM-dd', new Date());
  }
  return new Date(NaN);
}

/**
 * Constructs a simple 'yyyy-mm-dd' date string, completely ignoring timezone data if present.  This is useful for situations like the date form field,
 * where we don't want timezones getting in the way of selecting/setting dates via the picker.  Alternative to toISOString, which does UTC calculations.
 */
export function toSimpleDateString(date: Date | string): DateOnlyIsoString {
  if (isDate(date)) {
    if (!Number.isFinite((date).getTime())) return '' as DateOnlyIsoString; // Guard against invalid date objects
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
  // Ensure we are *only* working with the yyyy-mm-dd portion of the date string, ignore any time data if present.
  return isDateTimeIsoString(date) ? date.split('T')[0] : date;
}

/**
 * Constructs a simple 'yyyy-mm-ddT00:00:00Z' date string, completely ignoring timezone data if present.
 */
export function toSimpleDateTimeString(date: Date | string, timeOfDay: TimeOfDay): DateTimeIsoString {
  const simpleDateString = toSimpleDateString(date);
  const timeValue = timeOfDay === 'END_OF_DAY' ? 'T23:59:59Z' : 'T00:00:00Z';
  return isPopulatedString(simpleDateString) ? `${simpleDateString}${timeValue}` : '';
}

/** A simple proxy for new Date().  Seems silly, but Date() is difficult to mock in unit tests, so use this function that is much easier to mock. */
export function now(): Date {
  return new Date();
}

/**
 * A string representing the current date for use as initial form values,
 * using now() will get the date in UTC instead of local which may erroneously set the date picker to tomorrow
 * this function returns the local date in ISO format
 */
export function nowString(): string {
  return formatISO(now()).replace(/\.\d{3}/, '');
}

export function yesterdayAsString(): DateOnlyIsoString {
  return dayBeforeDateAsString(now());
}

export function dayBeforeDateAsString(date: Date): DateOnlyIsoString {
  // This is the date-fns-tz format(), not the standard one.  So after subtracting a day, the formatter will give us the right date in the local time zone.
  return format(subDays(date, 1), 'y-MM-dd');
}

export function daysSinceDate(date: Date): number {
  return differenceInDays(now(), date);
}

export function isOverdue(dueDate: DateOnlyIsoString) {
  // a date is only overdue if it's past the end of the day on that date, adjusted for local time
  return isPast(endOfDay(toLocalMidnight(dueDate)));
}

// "UNIT TESTS" for toLocalMidnight().  Until we have an easy way to write real, isolated unit tests:  uncomment this, and it'll run itself on browser refresh.
// (function () {
//   function testDate(header: string, date: Date): void {
//     console.group(header);
//     console.log('UTC date:', toDateTimeIsoString(date));
//     console.log('timezoneOffset:', date.getTimezoneOffset());
//     console.log('to local midnight as Date:', toDateTimeIsoString(toLocalMidnight(date)));
//     console.log('day before as string:', dayBeforeDateAsString(date));
//     console.groupEnd();
//   }
//
//   function testString(header: string, string: string): void {
//     console.group(header);
//     console.log('UTC string:', string);
//     console.log('to local midnight:', toDateTimeIsoString(toLocalMidnight(string)));
//     console.groupEnd();
//   }
//
//   {
//     console.group('toLocalMidnight() tests');
//     {
//       console.group('with Date input');
//       // Remember that Date.UTC()'s month parameter is zero-based. 🙄
//       testDate('1am UTC on January 1 - U.S. timezones should show the day before UTC', new Date(Date.UTC(2024, 0, 1, 1, 0, 0)));
//       testDate('1pm UTC on January 1 - U.S. timezones should show the same day as UTC', new Date(Date.UTC(2024, 0, 1, 13, 0, 0)));
//       testDate('1am UTC on June 1 - U.S. timezones should show the day before UTC', new Date(Date.UTC(2024, 5, 1, 1, 0, 0)));
//       testDate('1pm UTC on June 1 - U.S. timezones should show the same day as UTC', new Date(Date.UTC(2024, 5, 1, 13, 0, 0)));
//       testDate('now() - depends on when you run this!', now());
//       console.groupEnd();
//     }
//
//     {
//       console.group('with DateTimeIsoString input');
//       testString('1am UTC on January 1 - U.S. timezones should show the day before UTC', '2024-01-01T01:00:00Z');
//       testString('1pm UTC on January 1 - U.S. timezones should show the same day as UTC', '2024-01-01T13:00:00Z');
//       testString('1am UTC on June 1 - U.S. timezones should show the day before UTC', '2024-06-01T01:00:00Z');
//       testString('1pm UTC on June 1 - U.S. timezones should show the same day as UTC', '2024-06-01T13:00:00Z');
//       console.groupEnd();
//     }
//
//     {
//       console.group('with DateOnlyIsoString input');
//       testString('January 1 - U.S. timezones should show the day before UTC', '2024-01-01');
//       testString('June 1 - U.S. timezones should show the day before UTC', '2024-06-01');
//       console.groupEnd();
//     }
//     console.groupEnd();
//   }
// })();
